import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { TranslocoService } from "@jsverse/transloco";
import { Polyline } from "leaflet";

import { LorenEntityFormModel } from "../../../../shared/modules/loren-entity-form-card/interfaces/loren-entity-form-model.interface.ts";
import { LorenFieldTypes } from "../../../../shared/modules/loren-entity-form-card/enums/loren-field-types.enum";
import { LorenFormField } from "../../../../shared/modules/loren-entity-form-card/interfaces/loren-form-field.interface";
import { LorenAsset } from "../../../loren-assets/interfaces/loren-asset.interface";
import { FormFieldInteraction } from "../../../../shared/modules/loren-entity-form-card/components/loren-entity-form-card/interfaces/form-field-interaction.interface";

@Component({
    selector: "loren-trace-asset-selector-dialog",
    templateUrl: "./loren-trace-asset-selector-dialog.component.html",
    styleUrls: ["./loren-trace-asset-selector-dialog.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LorenTraceAssetSelectorDialogComponent implements OnInit {
  data = inject<{
    assets: LorenAsset[];
    assetTraceMap: Map<number, Polyline>;
}>(MAT_DIALOG_DATA);
  private translateService = inject(TranslocoService);
  private dialogRef = inject<MatDialogRef<LorenTraceAssetSelectorDialogComponent>>(MatDialogRef);

  formModel: LorenEntityFormModel;

  private allAssetsFormFields: LorenFormField[] = [];
  private assets: LorenAsset[] = [];
  private assetTraceMap: Map<number, Polyline> = new Map<number, Polyline>();
  private selectedAssetsForTracing: LorenAsset[] = [];
  private isCheckAll: boolean;

  constructor() {
    const data = this.data;

    this.assets = data.assets;
    this.assetTraceMap = data.assetTraceMap;
  }

  ngOnInit(): void {
    this.formModel = this.prepareAssetsSelectionFormModel();
  }

  handleFormInteractions(x: FormFieldInteraction) {
    if (!x) {
      return;
    }

    const assetField = this.assets.find((a) => a.id === Number(x.fieldName));

    if (x.fieldName === "searchAsset") {
      const searchAssetField = this.formModel.formFields.find(
        (f) => f.formControlType === LorenFieldTypes.INPUT
      );
      const checkAll = this.formModel.formFields.find(
        (f) => f.name === "checkAll"
      );
      let assetFields = this.allAssetsFormFields.filter(
        (f) =>
          f.formControlType === LorenFieldTypes.CHECKBOX &&
          f.label.toLowerCase().includes(x.fieldCurrentValue.toLowerCase()) &&
          f.name !== "checkAll"
      );

      if (assetFields.length === 0) {
        assetFields = [];
      }

      assetFields = [searchAssetField, checkAll, ...assetFields];

      this.formModel.formFields = assetFields;
    }

    if (x.fieldName === "checkAll") {
      if (x.fieldCurrentValue === true) {
        this.formModel.formFields
          .filter(
            (s) =>
              s.name !== "checkAll" &&
              s.formControlType === LorenFieldTypes.CHECKBOX
          )
          .forEach((s) => (s.value = true));
      } else {
        this.formModel.formFields
          .filter(
            (s) =>
              s.name !== "checkAll" &&
              s.formControlType === LorenFieldTypes.CHECKBOX
          )
          .forEach((s) => (s.value = null));
      }

      this.isCheckAll = x.fieldCurrentValue;
    }

    if (assetField && x.fieldCurrentValue) {
      this.selectedAssetsForTracing.push(assetField);
    } else if (assetField && !x.fieldCurrentValue) {
      this.selectedAssetsForTracing.splice(
        this.selectedAssetsForTracing.findIndex(
          (a) => a.id === Number(x.fieldName)
        ),
        1
      );
    }
  }

  continue() {
    if (this.isCheckAll === true) {
      this.selectedAssetsForTracing = this.assets.filter((x) => {
        return this.formModel.formFields.find((f) => Number(f.name) === x.id);
      });
    }

    this.dialogRef.close({
      selectedAssetsForTracing: this.selectedAssetsForTracing,
    });
  }

  private prepareAssetsSelectionFormModel() {
    const formModel: LorenEntityFormModel = {
      formFields: [
        {
          name: "searchAsset",
          label: "operations.search",
          placeholder: "operations.input_asset_name",
          formControlType: LorenFieldTypes.INPUT,
        },
      ],
      formName: "",
    };

    this.prepareAllAssetsSelectionFields();

    formModel.formFields.push(...this.allAssetsFormFields);

    return formModel;
  }

  private prepareAllAssetsSelectionFields() {
    this.allAssetsFormFields = [
      {
        name: `checkAll`,
        label: this.translateService.translate("operations.select_all"),
        value: null,
        formControlType: LorenFieldTypes.CHECKBOX,
      },
    ];

    this.assets.forEach((asset) => {
      let label = `${asset.name}`;

      if (asset.groups && asset.groups.length > 0) {
        label += ` (${asset.groups.join(",")})`;
      }

      const assetField: LorenFormField = {
        name: String(asset.id),
        label,
        value: this.assetTraceMap.has(asset.id) ? "checked" : null,
        formControlType: LorenFieldTypes.CHECKBOX,
      };

      // if second time this dialog is opened and there was already a selection
      if (this.assetTraceMap.has(asset.id)) {
        this.selectedAssetsForTracing.push(asset);
      }

      this.allAssetsFormFields.push(assetField);
    });
  }
}
